import dayjs, { Dayjs } from 'dayjs'
import { CreateScheduledEventsByJobV2Request, CreateByJobCadenceGroup, CreateByJobEventItem, RepeatingEventConfig, RescheduleEventNewEvent, SchedulingJob } from '../scheduleApi'
import { CadenceFormData } from '../EventForm/recurringScheduleModel'
import { expandRecurrenceDates } from './recurrenceExpander'
import { Duration } from '../../common/durationModel'

export type ExpandedEvent = {
  key: number
  cadenceKey: number
  startDate: Dayjs
  endDate: Dayjs
  startTime: Dayjs
  duration: Duration
  isAllDay: boolean
  crewMemberIds: number[]
  crewLeadId: number | null
  notes: string | null
}

export type CadenceData = {
  cadenceKey: number
  repeatingId: string | null
  repeatingEventConfig: RepeatingEventConfig | null
  workareaIds: string
  sendCrewLeadNotifications: boolean
  events: ExpandedEvent[]
}

const buildEventItem = (event: ExpandedEvent): CreateByJobEventItem => {
  let start: Dayjs
  let end: Dayjs
  let durationMinutes: number

  if (event.isAllDay) {
    start = event.startDate.startOf('day')
    end = event.endDate.startOf('day')
    durationMinutes = end.diff(start, 'minutes')
  } else {
    start = event.startDate
      .hour(event.startTime.hour())
      .minute(event.startTime.minute())
      .second(0)
    durationMinutes = Math.floor(event.duration.seconds / 60)
    end = start.add(durationMinutes, 'minutes')
  }

  return {
    start: start.toISOString(),
    end: end.toISOString(),
    allDay: event.isAllDay,
    estimatedDurationInMinutes: durationMinutes,
    crewLeadId: event.crewLeadId,
    crewMemberIds: event.crewMemberIds.length > 0 ? event.crewMemberIds.join(',') : null,
    notes: event.notes,
  }
}

export const buildRescheduleNewEvent = (
  job: SchedulingJob,
  cadences: CadenceData[]
): RescheduleEventNewEvent | null => {
  if (!job?.jobId || cadences.length === 0) {
    return null
  }
  const cadence = cadences[0]
  const event = cadence.events[0]
  if (!event) {
    return null
  }
  const eventItem = buildEventItem(event)
  return {
    jobId: job.jobId,
    start: eventItem.start,
    end: eventItem.end,
    dayStartTime: null,
    allDay: eventItem.allDay,
    estimatedDurationInMinutes: eventItem.estimatedDurationInMinutes,
    crewLeadId: eventItem.crewLeadId,
    crewMemberIds: eventItem.crewMemberIds,
    workAreaIds: cadence.workareaIds || null,
    routeId: null,
    repeatingEventConfig: null,
    notifications: cadence.sendCrewLeadNotifications,
    notes: eventItem.notes,
  }
}

export const buildCreateEventsV2Payload = (
  job: SchedulingJob,
  cadences: CadenceData[]
): CreateScheduledEventsByJobV2Request | null => {
  if (!job?.jobId || cadences.length === 0) {
    return null
  }

  const cadenceGroups: CreateByJobCadenceGroup[] = cadences.map(cadence => ({
    repeatingId: cadence.repeatingId,
    repeatingEventConfig: cadence.repeatingEventConfig,
    workareaIds: cadence.workareaIds || null,
    sendCrewLeadNotifications: cadence.sendCrewLeadNotifications,
    events: cadence.events.map(buildEventItem),
  }))

  return {
    jobId: job.jobId,
    hasApprovedWorkareas: job.hasApprovedWorkareas,
    cadences: cadenceGroups,
  }
}

export const buildExpandedEvent = (cadenceKey: number, formValues: CadenceFormData['formValues'], date: Dayjs, nextKeyRef: { current: number }): ExpandedEvent => ({
  key: nextKeyRef.current++,
  cadenceKey,
  startDate: date,
  endDate: formValues.endDate,
  startTime: formValues.startTime,
  duration: formValues.estimatedDuration,
  isAllDay: formValues.isAllDay,
  crewMemberIds: formValues.selectedCrew.map(c => c.id),
  crewLeadId: formValues.crewLeadId,
  notes: formValues.notes || null,
})

export const expandCadenceEvents = (cadenceKey: number, formData: CadenceFormData, nextKeyRef: { current: number }): ExpandedEvent[] => {
  const { formValues, repeatingEventConfig } = formData

  if (!repeatingEventConfig) {
    return [buildExpandedEvent(cadenceKey, formValues, formValues.startDate, nextKeyRef)]
  }

  const isOccurrencesMode = formValues.repeatEndType === 'occurrences'
  const endDate = isOccurrencesMode
    ? formValues.repeatEndsOn || formValues.startDate.add(formValues.repeatOccurrences * formValues.repeatCount, formValues.repeatUnit as dayjs.ManipulateType)
    : formValues.repeatEndsOn

  let dates = expandRecurrenceDates({
    startDate: formValues.startDate,
    endDate,
    repeatUnits: repeatingEventConfig.repeatUnits,
    repeatCount: formValues.repeatCount,
    repeatingDayUnit: repeatingEventConfig.repeatingDayUnit || formValues.startDate.format('dddd').toLowerCase(),
    repeatPlace: repeatingEventConfig.repeatPlace,
    repeatMonth: repeatingEventConfig.repeatMonth,
  })

  if (isOccurrencesMode && dates.length > formValues.repeatOccurrences) {
    dates = dates.slice(0, formValues.repeatOccurrences)
  }

  return dates.map(date => buildExpandedEvent(cadenceKey, formValues, date, nextKeyRef))
}
